import { useRoute } from '@react-navigation/native';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { ScrollView } from 'react-native';
import Button from 'src/components/10-atoms/button/Button';
import HTMLEditor from 'src/components/10-atoms/form/inputs/editor/html/HTMLEditor/Editor';
import HTMLRenderer from 'src/components/10-atoms/form/inputs/editor/html/HTMLEditor/Renderer';
import FeatherIcon from 'src/components/10-atoms/icons/FeatherIcon';
import Column from 'src/components/10-atoms/layout/Column';
import ReadingColumn from 'src/components/10-atoms/layout/ReadingColumn';
import Row from 'src/components/10-atoms/layout/Row';
import BackdropLoader from 'src/components/10-atoms/loaders/BackdropLoader';
import { PRESSABLE_SIZE } from 'src/components/10-atoms/pressables/constants';
import Tab from 'src/components/10-atoms/tabs/Tab';
import Tabs from 'src/components/10-atoms/tabs/Tabs';
import { overlayHelperViewActions } from 'src/components/20-helper-views/overlay/useOverlay';
import useTheme from 'src/customizations/themes/hooks/useTheme';
import styleUtils from 'src/utils/styles';
import NoteTabNavButton from './components/NoteTabNavButton';
import NoteTabs from './components/NoteTabs';
import NoteTabHistoryOverlay from './containers/NoteTabHistoryOverlay';
import useData from './useData';
const NOTE_EDITOR_NOTE_TABS_ID = 'note-editor-note-tabs';
const NOTE_EDITOR_NOTE_TAB_HISTORY_ID = 'note-editor-note-tab-history';
const NoteEditorContainer = forwardRef(({ note, editable = true, showHistoryAction = true, draftOptions, ...columnProps }, ref) => {
    const [{ deviceType, dimensions, theme }] = useTheme();
    const route = useRoute();
    const [state, actions, helpers] = useData(note, draftOptions);
    const tabHtmlEditorRefs = useRef({});
    const buildTabTo = (tabID) => !editable
        ? {
            screen: route.name,
            params: { ...route.params, tabID },
        }
        : undefined;
    useImperativeHandle(ref, () => ({
        saveNote: actions.saveNote,
        editNote: actions.editNote,
    }));
    const handleViewHistory = (tab) => {
        overlayHelperViewActions.show({
            id: NOTE_EDITOR_NOTE_TAB_HISTORY_ID,
            component: (React.createElement(NoteTabHistoryOverlay, { note: note, noteTabID: tab.id, onClose: () => overlayHelperViewActions.hide(NOTE_EDITOR_NOTE_TAB_HISTORY_ID), onRevert: (noteTabHistoryItem) => {
                    const html = noteTabHistoryItem.content ?? '';
                    actions.draft.updateNoteTabContent(tab.id, html);
                    tabHtmlEditorRefs.current[tab.id]?.setHTML(html);
                    overlayHelperViewActions.hide(NOTE_EDITOR_NOTE_TAB_HISTORY_ID);
                } })),
            direction: 'from-bottom',
        });
    };
    const onViewHistory = showHistoryAction ? handleViewHistory : undefined;
    const noteTabs = helpers.draft.getNoteTabs();
    const noteTabsElement = (React.createElement(NoteTabs, { style: !deviceType.isPhone ? { borderRightWidth: 1 } : undefined, tabs: noteTabs, editable: editable, activeTabID: state.activeTabID, onTabChange: (tabID) => {
            actions.setSelectedTab(tabID);
            overlayHelperViewActions.hide(NOTE_EDITOR_NOTE_TABS_ID);
        }, onAddTab: () => {
            actions.noteTab.addTab();
            overlayHelperViewActions.hide(NOTE_EDITOR_NOTE_TABS_ID);
        }, onEditTab: (tab) => {
            actions.noteTab.editTab(tab);
            overlayHelperViewActions.hide(NOTE_EDITOR_NOTE_TABS_ID);
        }, onDeleteTab: (tab) => {
            actions.noteTab.deleteTab(tab);
            overlayHelperViewActions.hide(NOTE_EDITOR_NOTE_TABS_ID);
        }, onViewHistory: onViewHistory ? (tab) => {
            handleViewHistory(tab);
            overlayHelperViewActions.hide(NOTE_EDITOR_NOTE_TABS_ID);
        } : undefined, onRearrangeTabs: editable ? actions.draft.rearrangeNoteTabs : undefined, getTabTo: buildTabTo }));
    const renderNoteTabsOverlay = () => {
        overlayHelperViewActions.show({
            id: NOTE_EDITOR_NOTE_TABS_ID,
            component: noteTabsElement,
            direction: 'from-right',
            style: { width: Math.min(dimensions.device.width - 72, 280) },
        });
    };
    return (React.createElement(Column, { flex: true, ...columnProps },
        React.createElement(Row, { flex: true },
            !deviceType.isPhone && (React.createElement(Column, { style: { width: 280 } }, noteTabsElement)),
            React.createElement(Tabs, { activeTab: state.activeTabID, behaviour: "hide", childContainerProps: { flex: true } }, helpers.draft.getNoteTabs().map((tab, index) => {
                const content = helpers.draft.getNoteTabContent(tab.id);
                const previousTab = helpers.draft.getPreviousNoteTab(index);
                const nextTab = helpers.draft.getNextNoteTab(index);
                return (React.createElement(Tab, { key: tab.id, id: tab.id },
                    React.createElement(Column, { flex: true }, !editable
                        ? (React.createElement(ScrollView, { contentContainerStyle: [styleUtils.flex, styleUtils.floatingBtnGutter] },
                            React.createElement(ReadingColumn, { ph: theme.spacing.page, pt: theme.spacing.standard.md, pb: theme.spacing.standard.xl, spacingY: theme.spacing.standard.md },
                                deviceType.isPhone && previousTab && (React.createElement(NoteTabNavButton, { direction: "previous", title: previousTab.data.title, onPress: () => actions.setSelectedTab(previousTab.id) })),
                                React.createElement(HTMLRenderer, { html: content }),
                                React.createElement(Column, { style: { height: 50 } }),
                                deviceType.isPhone && nextTab && (React.createElement(NoteTabNavButton, { direction: "next", title: nextTab.data.title, onPress: () => actions.setSelectedTab(nextTab.id) })))))
                        : (React.createElement(HTMLEditor, { ref: (instance) => {
                                if (instance) {
                                    tabHtmlEditorRefs.current[tab.id] = instance;
                                }
                                else {
                                    delete tabHtmlEditorRefs.current[tab.id];
                                }
                            }, key: tab.id, flex: true, initialValue: content, onChange: editable ? ({ html }) => actions.draft.updateNoteTabContent(tab.id, html) : undefined })))));
            }))),
        deviceType.isPhone && (React.createElement(Button, { style: { bottom: PRESSABLE_SIZE + theme.spacing.page }, variant: 'float', onPress: renderNoteTabsOverlay },
            React.createElement(FeatherIcon, { name: "menu" }))),
        state.updateNoteMutation.loading && React.createElement(BackdropLoader, null)));
});
export default NoteEditorContainer;
